import CollectionImage from '@/presentation/components/elements/CollectionImage';
import Icon, { Icons } from '@/presentation/components/elements/Icon';
import Price from '@/presentation/components/elements/Price';
import VaultIcon from '@/presentation/components/elements/VaultIcon';
import { useNetwork } from '@/presentation/context/network';
import useOnClickOutside from '@/presentation/hooks/useOnClickOutside';
import { getCollectionPath } from '@/presentation/utils/paths';
import Link from 'next/link';
import { Vault, matchVault } from 'nftx.js';
import { useRef, useState } from 'react';
import { useTranslations } from 'use-intl';

const Search = ({
  // tokens,
  // collections,
  vaults = [],
}: {
  vaults?: Vault[];
  // tokens: any[];
  // collections: any[];
}) => {
  const resultsRef = useRef(null);
  const { slug } = useNetwork();
  const t = useTranslations('Topbar.Search');
  const [search, setSearch] = useState<string>('');

  const showResults = !!search;

  useOnClickOutside(resultsRef, () => setSearch(''));

  const filteredVaults = !!search
    ? vaults?.filter((v) => matchVault(v, search))
    : vaults;

  const tokens = filteredVaults?.map((v) => {
    return {
      id: v.token.id,
      name: v.token.name,
      symbol: v.token.symbol,
      price: v.vTokenToEth,
      vaultId: v.vaultId,
    };
  });
  const collections = filteredVaults
    ?.map((v) => {
      return {
        id: v.asset.id,
        name: v.asset.name,
        slug: v.collectionSlug,
      };
    })
    .filter((v, i, self) => self.findIndex((o) => o.id === v.id) === i);

  return (
    <div className="relative">
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <Icon
          name={Icons.SEARCH}
          className="h-5 w-5 text-gray-400 transform transition-transform group-focus-within:scale-105"
          aria-hidden="true"
        />
      </div>
      <input
        type="search"
        name="nftx_v3_search"
        id="nftx_v3_search"
        className="h-10 md:h-12 bg-gray-200 block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-500 placeholder:focus:text-gray-400 text-gray-500 focus:ring-inset focus:ring-gray-300 text-sm"
        placeholder="Search"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />
      {showResults && (
        <div
          ref={resultsRef}
          className="absolute top-full left-2 right-2 sm:left-0 sm:right-auto sm:min-w-[28rem] rounded-md border border-gray-300 bg-gray-50 text-sm -mt-2 sm:mt-2 shadow-xl space-y-2 py-2 overflow-y-scroll hide-scroll max-h-80"
        >
          <section>
            <h3 className="text-gray-400 font-semibold text-xs uppercase my-2 mx-3">
              {t('collections')}
            </h3>
            <div className="space-y-1">
              {collections.length ? (
                collections.map((c) => (
                  <Link
                    href={getCollectionPath({
                      chain: slug,
                      collectionSlug: c.slug,
                    })}
                    key={c.id}
                    className="flex px-3 py-2 items-center hover:bg-gray-100"
                  >
                    <CollectionImage collection={c.id} className="w-6 mr-2" />
                    <h4 className="font-medium">{c.name}</h4>
                  </Link>
                ))
              ) : (
                <p className="px-6 text-center text-gray-500 py-4">
                  {'No matching collections in NFTX'}
                </p>
              )}
            </div>
          </section>
          <section className="">
            <h3 className="text-gray-400 font-semibold text-xs uppercase my-2 mx-3">
              {t('tokens')}
            </h3>
            <div className="space-y-1">
              {tokens.length ? (
                tokens.map((t) => (
                  <Link
                    href={`/${slug}/info/tokens/${t.id}`}
                    key={t.id}
                    className="flex px-3 py-2 items-center hover:bg-gray-100"
                  >
                    <VaultIcon vaultId={t.vaultId} className="w-6 mr-2" />
                    <h4 className="font-medium">{t.symbol}</h4>
                    <span className="ml-auto">
                      <Price value={t.price} />
                    </span>
                  </Link>
                ))
              ) : (
                <p className="px-6 text-center text-gray-500 py-4">
                  {'No matching tokens in NFTX'}
                </p>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default Search;
